import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  ScrollArea,
  Select,
  Text,
} from "@mantine/core";
import { IconDots, IconUsers } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { formatDate, formatTime } from "@util/dates";
import { useChat } from "./ChatContext";
import { conversationTitle, isSameActor, liveTypingFor } from "./chatHelpers";
import ChatComposer from "./ChatComposer";
import ChatMessageAttachment from "./ChatMessageAttachment";
import { isImageContentType } from "./chatFilePreview";
import classes from "./ChatTray.module.css";

export default function ChatThread() {
  const { t } = useTranslation();
  const {
    me,
    activeConversation,
    messages,
    loadingThread,
    conversationIsOnline,
    contacts,
    addPeople,
    leaveActive,
    typingMap,
  } = useChat();
  const bottomRef = useRef(null);
  const [addId, setAddId] = useState(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, activeConversation?.id, typingMap]);

  if (!activeConversation) {
    return (
      <div className={classes.threadPane}>
        <Box p="md">
          <Text c="dimmed">{t("chat_select_thread")}</Text>
        </Box>
      </div>
    );
  }

  const typing = liveTypingFor(typingMap, activeConversation.id);
  const isGroup =
    Number(activeConversation.variety) === 2 ||
    activeConversation.variety_formatted === "group";
  const online = conversationIsOnline(activeConversation);
  const addOptions = (contacts || [])
    .filter(
      (contact) =>
        !(activeConversation.participants || []).some((row) =>
          isSameActor(row, contact)
        )
    )
    .map((contact) => ({
      value: `${contact.kind}:${contact.id}`,
      label: contact.full_name || contact.email,
    }));

  return (
    <div className={classes.threadPane}>
      <Group
        justify="space-between"
        p="sm"
        style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}
      >
        <div>
          <Group gap={8}>
            <span className={`${classes.dot} ${online ? classes.dotOnline : ""}`} />
            <Text fw={600}>{conversationTitle(activeConversation, me, t)}</Text>
          </Group>
          <Text size="xs" c="dimmed">
            {online ? t("chat_online") : t("chat_offline")}
            {isGroup ? ` · ${(activeConversation.participants || []).length}` : ""}
          </Text>
        </div>
        {isGroup && (
          <Menu>
            <Menu.Target>
              <ActionIcon variant="subtle" aria-label={t("chat_leave")}>
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" onClick={() => leaveActive()}>
                {t("chat_leave")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {isGroup && addOptions.length > 0 && (
        <Group p="xs" gap="xs">
          <Select
            size="xs"
            placeholder={t("chat_add_people")}
            data={addOptions}
            value={addId}
            onChange={setAddId}
            searchable
            leftSection={<IconUsers size={14} />}
            style={{ flex: 1 }}
          />
          <Button
            size="xs"
            disabled={!addId}
            onClick={() => {
              const [kind, id] = addId.split(":");
              addPeople({ kind, id }).then(() => setAddId(null));
            }}
          >
            {t("add")}
          </Button>
        </Group>
      )}
      <ScrollArea className={classes.messages} offsetScrollbars>
        <div className={classes.messageList}>
          {loadingThread && (
            <Text size="sm" c="dimmed">
              {t("loading")}
            </Text>
          )}
          {messages.map((message) => {
            const mine = isSameActor(message.sender, me);
            const assets = message.assets || [];
            const imageAssets = assets.filter((asset) =>
              isImageContentType(asset.content_type)
            );
            const fileAssets = assets.filter(
              (asset) => !isImageContentType(asset.content_type)
            );
            const hasTextBubble = Boolean(message.body || fileAssets.length);
            const showSender = !mine && message.sender?.full_name;

            return (
              <div
                key={message.id}
                className={`${classes.messageBlock} ${mine ? classes.messageMine : classes.messageTheirs}`}
              >
                {showSender && !hasTextBubble && (
                  <Text size="xs" fw={600} mb={4}>
                    {message.sender.full_name}
                  </Text>
                )}
                {hasTextBubble && (
                  <Box
                    className={`${classes.bubble} ${mine ? classes.mine : classes.theirs}`}
                  >
                    {showSender && (
                      <Text size="xs" fw={600} mb={6}>
                        {message.sender.full_name}
                      </Text>
                    )}
                    {message.body && <Text size="sm">{message.body}</Text>}
                    {fileAssets.map((asset) => (
                      <ChatMessageAttachment
                        key={asset.id}
                        asset={asset}
                        mine={mine}
                      />
                    ))}
                    {!imageAssets.length && (
                      <Text size="xs" opacity={0.7} className={classes.bubbleMeta}>
                        {formatDate(message.created_at)}{" "}
                        {formatTime(message.created_at)}
                      </Text>
                    )}
                  </Box>
                )}
                {imageAssets.map((asset) => (
                  <ChatMessageAttachment
                    key={asset.id}
                    asset={asset}
                    mine={mine}
                  />
                ))}
                {imageAssets.length > 0 && (
                  <Text size="xs" c="dimmed" className={classes.mediaMeta}>
                    {formatDate(message.created_at)} {formatTime(message.created_at)}
                  </Text>
                )}
              </div>
            );
          })}
          {typing && (
            <Box
              className={`${classes.bubble} ${classes.theirs} ${classes.typingBubble}`}
              aria-label={t("chat_typing")}
            >
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
            </Box>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
      <ChatComposer />
    </div>
  );
}
